import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';

interface DashboardCard {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  count?: number;
}

interface Course {
  id: string;
  title: string;
  status: string;
  level: string;
  subject: string;
}

interface Project {
  id: string;
  title: string;
  status: string;
  progress: number;
  deadline: string;
}

const LCMSDashboard: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useLanguage();
  
  // Define typed state variables
  const [courses, setCourses] = useState<Course[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    // Simulate data loading
    const loadData = async () => {
      setIsLoading(true);
      
      // In a real implementation, you would fetch data from an API
      setTimeout(() => {
        setCourses([
          {
            id: '1',
            title: 'Mathematics Grade 10',
            status: 'published',
            level: '10',
            subject: 'Mathematics',
          },
          {
            id: '2',
            title: 'Physics Grade 11',
            status: 'draft',
            level: '11',
            subject: 'Physics',
          },
          {
            id: '3',
            title: 'Literature Grade 9',
            status: 'pending_review',
            level: '9',
            subject: 'Literature',
          },
        ]);
        
        setProjects([
          {
            id: '1',
            title: 'Science Curriculum Review',
            status: 'active',
            progress: 75,
            deadline: '2023-12-15',
          },
          {
            id: '2',
            title: 'Math Textbook Development',
            status: 'active',
            progress: 45,
            deadline: '2025-01-30',
          },
          {
            id: '3',
            title: 'Language Arts Content Update',
            status: 'planning',
            progress: 20,
            deadline: '2025-02-28',
          },
        ]);
        
        setIsLoading(false);
      }, 1000);
    };

    loadData();
  }, []);

  if (isLoading) {
    return <div className="container mx-auto py-6 text-center">{t('common.loading')}</div>;
  }

  const dashboardCards: DashboardCard[] = [
    {
      title: t('course-content.allCourses'),
      description: t('dashboard.overview'),
      href: '/lcms/course-content',
      count: courses.length,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      )
    },
    {
      title: t('course-content.createCourse'),
      description: t('dashboard.createNew'),
      href: '/lcms/course-content/new',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: 'Content Bank',
      description: 'Manage your reusable content',
      href: '/lcms/content',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      )
    }
  ];

  const recentCourses = courses.slice(0, 5);
  const recentProjects = projects.slice(0, 5);

  const getProgressIndicator = (progress: number) => {
    const width = `${progress}%`;
    
    return (
      <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
        <div 
          className="bg-blue-600 h-2.5 rounded-full" 
          style={{ width }}
        ></div>
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="container mx-auto py-6">
      <h1 className="text-2xl font-bold mb-6">{t('app.name')}</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {dashboardCards.map((card, index) => (
          <Link key={index} to={card.href} className="block">
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-center mb-4">
                <div className="p-3 bg-blue-100 text-blue-800 rounded-full">
                  {card.icon}
                </div>
                {card.count !== undefined && (
                  <span className="text-2xl font-bold">{card.count}</span>
                )}
              </div>
              <h2 className="text-lg font-semibold mb-1">{card.title}</h2>
              <p className="text-gray-600">{card.description}</p>
            </div>
          </Link>
        ))}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">{t('course-content.title')}</h2>
            <Link to="/lcms/course-content" className="text-blue-600 hover:underline text-sm">
              {t('dashboard.viewAll')}
            </Link>
          </div>
          
          {recentCourses.length === 0 ? (
            <p className="text-gray-500">No courses available</p>
          ) : (
            <div className="space-y-3">
              {recentCourses.map(course => (
                <Link key={course.id} to={`/lcms/course-content/${course.id}`} className="block">
                  <div className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between">
                      <h3 className="font-medium">{course.title}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        course.status === 'published' ? 'bg-green-100 text-green-800' :
                        course.status === 'pending_review' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {course.status === 'draft' ? t('dashboard.contentStatus.draft') : 
                         course.status === 'pending_review' ? t('dashboard.contentStatus.pendingApproval') : 
                         t('dashboard.contentStatus.published')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {t(`grades.grade${course.level}`)} · {t(`subjects.${course.subject.toLowerCase()}`)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Active Projects</h2>
            <Link to="/lcms/projects/active" className="text-blue-600 hover:underline text-sm">
              {t('dashboard.viewAll')}
            </Link>
          </div>
          
          {recentProjects.length === 0 ? (
            <p className="text-gray-500">No projects available</p>
          ) : (
            <div className="space-y-3">
              {recentProjects.map(project => (
                <Link key={project.id} to={`/lcms/projects/${project.id}`} className="block">
                  <div className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between">
                      <h3 className="font-medium">{project.title}</h3>
                      <span className="text-sm text-gray-500">
                        {project.progress}% complete
                      </span>
                    </div>
                    {getProgressIndicator(project.progress)}
                    <div className="flex justify-between text-sm text-gray-500 mt-2">
                      <span className={`px-2 py-0.5 rounded-full ${
                        project.status === 'active' ? 'bg-green-100 text-green-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {project.status}
                      </span>
                      <span>Deadline: {formatDate(project.deadline)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LCMSDashboard; 